import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { withTranslation } from 'react-i18next';
import { titleize } from 'underscore.string';

import CustomerSearchWithPreferences from 'components/site/customer-search-with-preferences';
import { getCarrierPreferencesByType } from 'utils/CarrierPreferencesUtils';
import TripStop from './TripStop';
import { sluggify } from '../../../../utils/localization/utils';

class Leg extends Component {
  static propTypes = {
    leg: PropTypes.shape({
      carrier: PropTypes.string,
      carrier_id: PropTypes.string,
      sequence: PropTypes.number.isRequired,
      stops: PropTypes.arrayOf(
        PropTypes.shape({
          sequence: PropTypes.number.isRequired,
          action: PropTypes.oneOf(['pickup', 'deliver']),
          location: PropTypes.shape({
            name: PropTypes.string,
            address: PropTypes.string,
          }),
          empty: PropTypes.bool,
        }),
      ),
    }).isRequired,
    shipperId: PropTypes.string,
    validationResult: PropTypes.object,
    onCarrierFieldChange: PropTypes.func.isRequired,
    onCarrierRemove: PropTypes.func.isRequired,
    onCarrierSelect: PropTypes.func.isRequired,
    onLegEquipmentAdd: PropTypes.func.isRequired,
    onLegRemove: PropTypes.func.isRequired,
    onLegTypeChange: PropTypes.func.isRequired,
    onStatusChange: PropTypes.func.isRequired,
    onStopAdd: PropTypes.func.isRequired,
    onStopEquipmentAdd: PropTypes.func.isRequired,
    onStopEquipmentRemove: PropTypes.func.isRequired,
    onStopIntentSelect: PropTypes.func.isRequired,
    onStopLocationNameChange: PropTypes.func.isRequired,
    onStopLocationAddressChange: PropTypes.func.isRequired,
    onStopLocationSelect: PropTypes.func.isRequired,
    onStopRemove: PropTypes.func.isRequired,
  };

  static defaultProps = {
    validationResult: {
      stops: [],
    },
  };

  constructor(props) {
    super(props);
    this.state = {
      hovering: false,
      carrierPreferences: {
        preferred: [],
        excluded: [],
      },
    };
  }

  componentDidMount() {
    const { shipperId } = this.props;
    this.getCarrierPreferences(shipperId);
  }

  getCarrierPreferences = async (shipperId) => {
    const carrierPreferences = await getCarrierPreferencesByType(
      shipperId,
      'drayage',
    );
    this.setState({ carrierPreferences });
  };

  handleDragOver = (e) => {
    e.preventDefault();
    this.setState({
      hovering: true,
    });
  };

  handleDragLeave = () => this.setState({ hovering: false });

  handleDrop = (e) => {
    const equipmentId = e.dataTransfer.getData('id');
    e.stopPropagation();
    if (equipmentId) {
      this.props.onLegEquipmentAdd(this.props.leg.sequence, equipmentId);
    }

    this.setState({ hovering: false });
  };

  handleCarrierFieldChange = (value) =>
    this.props.onCarrierFieldChange(this.props.leg.sequence, value);

  handleCarrierRemove = () =>
    this.props.onCarrierRemove(this.props.leg.sequence);

  handleCarrierSelect = (value, item) =>
    this.props.onCarrierSelect(this.props.leg.sequence, item);

  handleLegRemove = () => this.props.onLegRemove(this.props.leg.sequence);

  handleLegTypeChange = (shipment_type) => () =>
    this.props.onLegTypeChange(this.props.leg.sequence, shipment_type);

  handleStopAdd = (e) => {
    e.preventDefault();
    const {
      leg: { sequence: legSequence },
    } = this.props;
    this.props.onStopAdd(legSequence);
  };

  handleStopEquipmentDrop = (stopSequence, equipmentId) => {
    this.setState({
      hovering: false,
    });

    this.props.onStopEquipmentAdd(
      this.props.leg.sequence,
      stopSequence,
      equipmentId,
    );
  };

  handleStopEquipmentRemove = (stopSequence, equipmentId) =>
    this.props.onStopEquipmentRemove(
      this.props.leg.sequence,
      stopSequence,
      equipmentId,
    );

  handleStopLocationNameChange = (stopSequence, value) =>
    this.props.onStopLocationNameChange(
      this.props.leg.sequence,
      stopSequence,
      value,
    );

  handleStopLocationAddressChange = (stopSequence, value) =>
    this.props.onStopLocationAddressChange(
      this.props.leg.sequence,
      stopSequence,
      value,
    );

  handleStopIntentSelect = (sequence, intent) =>
    this.props.onStopIntentSelect(this.props.leg.sequence, sequence, intent);

  handleStopLocationSelect = (stopSequence, value) =>
    this.props.onStopLocationSelect(
      this.props.leg.sequence,
      stopSequence,
      value,
    );

  handleStopRemove = (stopSequence) =>
    this.props.onStopRemove(this.props.leg.sequence, stopSequence);

  renderStops() {
    const {
      leg: { stops },
      t,
    } = this.props;

    const validationResult = this.props.validationResult || {
      stops: [],
    };
    return (
      <div className="">
        <h4 className="mb-4 text-base">{titleize(t('STOP', { count: 2 }))}</h4>
        <div className="mb-4">
          {stops.length > 0 &&
            stops.map((stop, i) => (
              <TripStop
                key={stop.sequence}
                stop={stop}
                validationResult={validationResult.stops[i]}
                onEquipmentDrop={this.handleStopEquipmentDrop}
                onEquipmentRemove={this.handleStopEquipmentRemove}
                onLocationSelect={this.handleStopLocationSelect}
                onRemove={this.handleStopRemove}
                onLocationNameChange={this.handleStopLocationNameChange}
                onLocationAddressChange={this.handleStopLocationAddressChange}
                onIntentSelect={this.handleStopIntentSelect}
              />
            ))}
          {stops.length === 0 && (
            <div className="">
              <p>{t('CREATE_A_STOP')}</p>
            </div>
          )}
        </div>
        <button
          className="button button-hollow-green"
          onClick={this.handleStopAdd}
        >
          <i className="mr-2 fa fa-plus" />
          <span>{titleize(t('STOP', { count: 2 }))}</span>
        </button>
      </div>
    );
  }

  renderTypeButton(type) {
    const {
      leg: { shipment_type: selectedType },
      t,
    } = this.props;

    const active = type === selectedType;
    const className = classnames({
      'cursor-pointer': true,
      rounded: true,
      'bg-white': true,
      border: true,
      'border-grey': true,
      'hover:border-green': true,
      'border-green': active,
      'border-transparent': !active,
      'p-4': true,
      'mr-4': true,
      'text-sm': true,
      'text-white': active,
      'bg-green-400': active,
    });

    return (
      <div>
        <button className={className} onClick={this.handleLegTypeChange(type)}>
          {titleize(t(sluggify(type)))}
        </button>
      </div>
    );
  }

  render() {
    const {
      leg: { sequence, carrier_id, carrier },
      t,
    } = this.props;

    const { hovering, carrierPreferences } = this.state;

    const className = classnames({
      'bg-white': true,
      'p-4': true,
      rounded: true,
      relative: true,
      'border-transparent': !hovering,
      'border-green': hovering,
      'border-solid': hovering,
      border: true,
    });

    const carrierSelected = !!carrier_id;
    const carrierFieldDisabled = carrierSelected;
    const carrierName = carrier || '';
    return (
      <div
        className={className}
        onDragOver={this.handleDragOver}
        onDragLeave={this.handleDragLeave}
        onDrop={this.handleDrop}
      >
        <h4 className="mb-4 text-xl">{t('LEG_HASH_SEQUENCE', { sequence })}</h4>
        <div className="flex flex-wrap justify-between mb-4">
          <div className="flex mb-4">
            {this.renderTypeButton('drayage')}
            {this.renderTypeButton('rail')}
          </div>
        </div>

        <div className="flex items-center mb-4 customer-search">
          <CustomerSearchWithPreferences
            clearOnSelect={false}
            onChange={this.handleCarrierFieldChange}
            onSelect={this.handleCarrierSelect}
            placeholder={t('SEARCH_FOR_CARRIER')}
            disabled={carrierFieldDisabled}
            type="carriers"
            authorizedOnly
            defaultValue={carrierName}
            carrierPreferences={carrierPreferences}
            isDriver={false}
          />
          {carrierSelected && (
            <button
              className="flex items-center justify-center w-8 h-8 ml-2 text-red-700 rounded-full bg-red-50 hover:text-white"
              onClick={this.handleCarrierRemove}
            >
              <i className="mr-0 text-base fa fa-remove" />
            </button>
          )}
        </div>

        {this.renderStops()}
        <div className="absolute top-0 right-0 mt-6 mr-6">
          <button title={t('REMOVE_LEG')} onClick={this.handleLegRemove}>
            <i className="fa fa-close text-grey" />
          </button>
        </div>
        {this.state.hovering && (
          <div className="absolute top-0 left-0 flex items-center justify-center w-full h-full border-2 border-dashed rounded pointer-events-none border-green-darker bg-green-lighter-transparent text-green-darker">
            <h3 className="mb-0 text-2xl">{t('DRAG_EQUIPMENT_TO_ADD')}</h3>
          </div>
        )}
      </div>
    );
  }
}

export default withTranslation()(Leg);
